import { createSelector } from 'reselect'
import { isEmpty } from '../utils/common'
import { sortColumnByKey } from '../utils/sort'
import {
  STATUS_NEW,
  STATUS_DENIED,
  STATUS_NEEDS_INFO,
  ARTYPE_NONSTOCKEDSSD,
  ARTYPE_ANCILLARYMATERIALS,
  ARTYPE_SECURE_ANCILLARYMATERIALS,
  ARTYPE_NON_SECURE_ANCILLARYMATERIALS,
  ARTYPE_EXCEPTIONTESTING,
  ARTYPE_ORDERSAFTERDEADLINE,
  ARTYPE_RESHIPMENTS,
  SPECIAL_ORDER_TYPE_SECURE,
  SPECIAL_ORDER_TYPE_NON_SECURE,
  ARTYPE_OFFCYCLETESTING,
} from '../constants/ApprovalRequestConstants'
import { APPROVAL_REQUEST_FILTERS } from '../constants/FilterConstants'
import {
  denormalizeNonStockedApprovalRequests,
  denormalizeAncillaryMaterialsApprovalRequests,
  denormalizeExceptionTestingApprovalRequests,
  denormalizeOrdersAfterDeadlineApprovalRequests,
  denormalizeReshipmentsApprovalRequests,
  denormalizeOffCycleTestingApprovalRequests,
} from '../utils/normalize'

export const filterByOpen = data => data.filter(item => item?.etsApprovalStatus === STATUS_NEW)
export const filterByClosed = data => data.filter(item => item?.etsApprovalStatus !== STATUS_NEW)
export const filterByNeedsAction = data =>
  data.filter(
    item =>
      item?.etsApprovalStatus == STATUS_DENIED || item?.etsApprovalStatus === STATUS_NEEDS_INFO
  )
const filterByDistance = data => data.filter(item => item?.contiguousUSInd === false)
export const filterByAiCode = (data, aiCode) => data.filter(item => item?.aiCode.startsWith(aiCode))

const selectFilters = state => state.filters
const selectApprovalRequests = state => state.approvalRequests
export const selectSelectedApprovalRequestId = state =>
  state.approvalRequests.selectedApprovalRequest
export const selectApprovalRequestType = state => state.approvalRequests.arType
export const getApprovalRequestCloseFocusElem = state => state.approvalRequests.closeFocusElem
export const getApprovalRequestProcessing = state =>
  state.approvalRequests.processingApprovalRequest

export const selectFilteredApprovalRequests = createSelector(
  [selectApprovalRequests, selectFilters],
  (approvalRequests, filters) => {
    const { arType } = approvalRequests
    const { filter, groupByAICode, filterDistantSchools, filterAiCode } = filters[arType]
    let dataArr = []
    switch (arType) {
      case ARTYPE_NONSTOCKEDSSD:
        dataArr = denormalizeNonStockedApprovalRequests(approvalRequests)
        break

      case ARTYPE_ANCILLARYMATERIALS:
        dataArr = denormalizeAncillaryMaterialsApprovalRequests(approvalRequests)
        break

      case ARTYPE_EXCEPTIONTESTING:
        dataArr = denormalizeExceptionTestingApprovalRequests(approvalRequests)
        break
      
      case ARTYPE_OFFCYCLETESTING:
        dataArr = denormalizeOffCycleTestingApprovalRequests(approvalRequests)
        break

      case ARTYPE_ORDERSAFTERDEADLINE:
        dataArr = denormalizeOrdersAfterDeadlineApprovalRequests(approvalRequests)
        break
      case ARTYPE_RESHIPMENTS:
        dataArr = denormalizeReshipmentsApprovalRequests(approvalRequests)
        break
    }

    let visibleData = []
    switch (filter) {
      case APPROVAL_REQUEST_FILTERS.open.value:
        visibleData = filterByOpen(dataArr)
        break

      case APPROVAL_REQUEST_FILTERS.closed.value:
        visibleData = filterByClosed(dataArr)
        break

      case APPROVAL_REQUEST_FILTERS.all.value:
        visibleData = dataArr
    }

    if (filterDistantSchools) {
      visibleData = filterByDistance(visibleData)
    }
    if (groupByAICode) {
      visibleData = sortColumnByKey([...visibleData], 'aiCode', 'asc')
    }
    if (filterAiCode) {
      visibleData = filterByAiCode(visibleData, filterAiCode)
    }

    return visibleData
  }
)

export const selectSelectedApprovalRequest = createSelector(
  [selectApprovalRequests],
  approvalRequests => {
    const selectedId = approvalRequests.selectedApprovalRequest
    let selectedApprovalRequest = null

    if (!isEmpty(approvalRequests.entities) && selectedId !== null) {
      selectedApprovalRequest = approvalRequests.entities.approvalRequests[selectedId]
    }
    return selectedApprovalRequest
  }
)

export const selectNumApprovalRequests = createSelector(
  [selectApprovalRequests],
  approvalRequests => {
    return approvalRequests?.entities?.approvalRequests
      ? Object.keys(approvalRequests.entities?.approvalRequests).length
      : 0
  }
)

export const selectNumPendingApprovalRequests = createSelector(
  [selectApprovalRequests],
  approvalRequests => {
    return approvalRequests?.entities?.approvalRequests
      ? filterByOpen(Object.values(approvalRequests.entities?.approvalRequests)).length
      : 0
  }
)

export const selectApprovalRequestStatusProps = createSelector(
  [selectApprovalRequests],
  approvalRequests => {
    const { fetching, fetched, error } = approvalRequests
    return { fetching, fetched, error }
  }
)

export const selectSpecificApprovalRequestType = createSelector(
  [selectSelectedApprovalRequest, selectApprovalRequestType],
  (selectedApprovalRequest, arType) => {
    let specificARType
    switch (selectedApprovalRequest?.specialOrderType) {
      case SPECIAL_ORDER_TYPE_SECURE:
        specificARType = ARTYPE_SECURE_ANCILLARYMATERIALS
        break

      case SPECIAL_ORDER_TYPE_NON_SECURE:
        specificARType = ARTYPE_NON_SECURE_ANCILLARYMATERIALS
        break

      default:
        specificARType = arType
        break
    }
    return specificARType
  }
)
